/**
 * A person: who they are and where they came from, their story (what they did, what they wrote, the question that
 * waits for you), how far they watched each room, their tags, automations and your notes. One row of actions.
 */
import { Fragment, useState } from "react";
import { Link } from "react-router";
import type { JourneyItem, PersonDetailResponse } from "../../../shared/types";
import { Button } from "../../components/ui/Button";
import { Ring } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Menu } from "../../components/ui/Menu";
import { Pill } from "../../components/ui/Pill";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";
import { clock, dayMonth, firstName } from "../../lib/when";
import { depthOf } from "./list";
import { byDay, dotOf, PIN_WHAT, systemTagsOf, useAddNote, useAddTag, usePerson, usePersonMenu } from "./person";
import { AutomationSheet, DeleteSheet, NoteSheet } from "./sheets";
import { StagePill } from "./stage";
import "../../styles/people.css";

export function PersonPage({ id }: { id: string }) {
  const { d, failed, reload, openThread } = usePerson(id);
  const [noting, setNoting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const menu = usePersonMenu(d, { automation: () => setAdding(true), remove: () => setDeleting(true) });

  if (!d) {
    return (
      <div className="page">
        <Link className="back" to="/people">
          <Icon name="left" /> People
        </Link>
        <section className="card pad">
          {failed ? (
            <div className="row g12">
              <span className="sp">This person didn't load.</span>
              <Button size="s" onClick={reload}>
                Try again
              </Button>
            </div>
          ) : (
            <div className="small muted">Loading</div>
          )}
        </section>
      </div>
    );
  }

  const p = d.registrant;
  const first = firstName(p.name);
  const facts = [p.email, d.phone, d.came_from ? `came from ${d.came_from}` : ""].filter(Boolean).join(" · ");
  const systemTags = systemTagsOf(d);

  return (
    <div className="page">
      <Link className="back" to="/people">
        <Icon name="left" /> People
      </Link>
      <section className="card phero">
        <Ring name={p.name} photo={p.avatar_url} size={96} percent={depthOf(p)} live={!!p.watching} />
        <div style={{ minWidth: 0 }}>
          <div className="row g8 wrap">
            <StagePill p={p} header />
            {d.own_tags.map((t) => (
              <span key={t} className="tag">
                {t}
              </span>
            ))}
          </div>
          <h1 className="mt8"><bdi>{p.name}</bdi></h1>
          <div className="muted mt4">{facts}</div>
        </div>
        <div className="row g8 pacts">
          <Button variant="p" icon="mail" onClick={() => setNoting(true)}>
            Send email
          </Button>
          <Button icon="chat" onClick={openThread}>
            View conversation
          </Button>
          <Menu label={`More for ${p.name}`} buttonClassName="bd" items={menu} />
        </div>
      </section>
      <div className="plyt2">
        <section className="card">
          <div className="card-h">
            <div>
              <h2>Their story</h2>
            </div>
            {p.watching ? (
              <Pill tone="good" watching>
                Watching now
              </Pill>
            ) : null}
          </div>
          <div className="story">
            {d.journey.length === 0 ? <div className="empty">Nothing yet.</div> : null}
            {/* Why one run of siblings: the line between the dots carries on across the days, to the last one. */}
            {byDay(d.journey).map(([day, items]) => (
              <Fragment key={day}>
                <div className="dayh">{day}</div>
                {items.map((e, i) => (
                  <StoryLine key={`${e.at}-${e.type}-${i}`} e={e} first={first} registrantId={id} />
                ))}
              </Fragment>
            ))}
          </div>
        </section>
        <div className="col" style={{ gap: 20 }}>
          {d.pins.map((pin) => (
            <div key={pin.id} className="note brass">
              <Icon name="pin" />
              <div>
                <b>{`Pinned on their page${pin.until_at ? ` until ${dayMonth(pin.until_at)}` : ""}`}</b>
                <br />
                {pin.title ?? PIN_WHAT[pin.target_kind]}
              </div>
            </div>
          ))}
          {d.answers.length ? (
            <section className="card">
              <div className="card-h">
                <div>
                  <h2>Their answers</h2>
                </div>
              </div>
              <div className="card-b col" style={{ gap: 8, paddingTop: 10 }}>
                {d.answers.map((a) => (
                  <div key={a.label} className="ans">
                    <span>{a.label}</span>
                    <b>{a.value}</b>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          <section className="card">
            <div className="card-h">
              <h2>Watch depth</h2>
            </div>
            <div className="card-b" style={{ paddingTop: 8 }}>
              {d.webinars.map((g) => (
                <div key={g.registration_id} className="wd">
                  <span className="small b6">{g.webinar}</span>
                  <b className="small">{`${g.watched_pct}%`}</b>
                  <div className={g.watched_pct >= 100 ? "bar goodb" : "bar"}>
                    <i style={{ width: `${g.watched_pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          <TagsCard id={p.id} own={d.own_tags} system={systemTags} onAdded={reload} />
          <section className="card">
            <div className="card-h">
              <h2>Automations</h2>
              <button type="button" className="link" onClick={() => setAdding(true)}>
                <Icon name="plus" /> Add
              </button>
            </div>
            <div className="card-b col" style={{ gap: 10, paddingTop: 12 }}>
              {d.automations.length === 0 ? <div className="small muted">Not in any yet.</div> : null}
              {d.automations.map((a, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: one person can run through one automation twice
                <div key={`${a.name}-${i}`} className="row g12">
                  <span className="pauto">
                    <Icon name="zap" />
                  </span>
                  <div>
                    <div className="b6 small">{a.name}</div>
                    <div className="xs muted">{a.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          <NotesCard id={p.id} notes={d.notes} onSaved={reload} />
        </div>
      </div>
      <NoteSheet person={noting ? { id: p.id, name: p.name, avatar_url: p.avatar_url, email: p.email } : null} pill={<StagePill p={p} header />} onClose={() => setNoting(false)} onSent={reload} />
      <AutomationSheet open={adding} ids={[p.id]} onClose={() => setAdding(false)} onAdded={reload} />
      <DeleteSheet open={deleting} id={p.id} name={p.name} onClose={() => setDeleting(false)} />
    </div>
  );
}

// ── one line of their story ─────────────────────────────────────────────
function StoryLine({ e, first, registrantId }: { e: JourneyItem; first: string; registrantId: string }) {
  const dot = dotOf(e);
  return (
    <div className="ev">
      <div className={dot.tone ? `dot ${dot.tone}` : "dot"}>
        <Icon name={dot.icon} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="row g8">
          <span className="t sp">{e.title}</span>
          <span className="when">{clock(e.at)}</span>
        </div>
        {e.detail ? <div className="d">{e.detail}</div> : null}
        {e.pct !== undefined ? (
          <div className={e.pct >= 100 ? "bar mt8 goodb" : "bar mt8"} style={{ maxWidth: 260 }}>
            <i style={{ width: `${e.pct}%` }} />
          </div>
        ) : null}
        {e.quote ? <div className="bubble mt12">{e.quote}</div> : null}
        {e.waiting && e.conversation_id ? <ReplyBox registrantId={registrantId} first={first} /> : null}
      </div>
    </div>
  );
}

/** Write back under the question that waits, on their page; Draft with AI fills it in. */
function ReplyBox({ registrantId, first }: { registrantId: string; first: string }) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  if (sent) {
    return (
      <div className="box">
        <div className="row g8 small">
          <b className="wine">You replied</b>
          <span className="muted">just now</span>
        </div>
        <div className="mt4">{sent}</div>
      </div>
    );
  }
  const draft = async () => {
    setBusy(true);
    try {
      const { contactId } = await api.talk.directFor(registrantId);
      setText((await api.talk.draftDirect(contactId)).draft);
    } catch {
      toast({ text: "AI could not write one just now. Try again.", icon: "info" });
    } finally {
      setBusy(false);
    }
  };
  const send = async () => {
    const v = text.trim();
    if (!v) return;
    setBusy(true);
    try {
      const { contactId } = await api.talk.directFor(registrantId);
      await api.talk.sendDirect(contactId, { channel: "chat", body: v, clientId: crypto.randomUUID() });
      setSent(v);
      toast({ text: `Sent to ${first}`, icon: "send" });
    } catch (err) {
      toast({ text: err instanceof ApiError ? err.message : "That didn't go. Try again.", icon: "info" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="reply">
      <textarea dir="auto" placeholder={`Write back to ${first}`} aria-label={`Write back to ${first}`} value={text} onChange={(e) => setText(e.target.value)} />
      <div className="bar2">
        <button type="button" className="aibtn" onClick={draft} disabled={busy}>
          <Icon name="sparkle" /> Draft with AI
        </button>
        <span className="sp" />
        <span className="xs muted">To their page</span>
        <Button size="s" variant="p" icon="send" onClick={send} disabled={busy || !text.trim()}>
          Send
        </Button>
      </div>
    </div>
  );
}

// ── tags and notes ──────────────────────────────────────────────────────
function TagsCard({ id, own, system, onAdded }: { id: string; own: string[]; system: string[]; onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const add = useAddTag(id, onAdded);
  const save = async () => {
    if (!(await add(value))) return;
    setValue("");
    setOpen(false);
  };
  return (
    <section className="card">
      <div className="card-h">
        <h2>Tags</h2>
        <button type="button" className="link" onClick={() => (open ? void save() : setOpen(true))}>
          <Icon name="plus" /> Add
        </button>
      </div>
      <div className="card-b">
        <div className="tagsbox">
          {own.map((t) => (
            <span key={t} className="tag">
              {t}
            </span>
          ))}
          {system.map((t) => (
            <span key={t} className="tag tsys">
              {t}
            </span>
          ))}
          {open ? (
            <span className="row g4">
              <input dir="auto"
                className="inp"
                placeholder="New tag"
                aria-label="New tag"
                // biome-ignore lint/a11y/noAutofocus: the field opens because they asked to type a tag
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void save();
                  if (e.key === "Escape") setOpen(false);
                }}
              />
              <Button size="s" onClick={() => void save()} style={{ height: 30 }}>
                Add
              </Button>
            </span>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function NotesCard({ id, notes, onSaved }: { id: string; notes: PersonDetailResponse["notes"]; onSaved: () => void }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = useAddNote(id, onSaved);
  const save = async () => {
    setBusy(true);
    if (await add(text)) setText("");
    setBusy(false);
  };
  return (
    <section className="card">
      <div className="card-h">
        <h2>Notes</h2>
      </div>
      <div className="card-b col" style={{ gap: 10, paddingTop: 12 }}>
        {notes.map((n) => (
          <div key={n.id} className="notep">
            {n.body}
            <div className="by">{`${firstName(n.author_name)}, ${dayMonth(n.at)}`}</div>
          </div>
        ))}
        <textarea dir="auto" className="inp" rows={2} placeholder="Add a note" aria-label="Add a note" value={text} onChange={(e) => setText(e.target.value)} />
        <Button size="s" style={{ alignSelf: "flex-end" }} onClick={save} disabled={busy || !text.trim()}>
          Save note
        </Button>
      </div>
    </section>
  );
}
